import type {
  AcquireBookPictures,
  AcquireBookZenbujiko,
  AcquireMultipleBookHandoffData,
  AllMattersAnalyzeOptions,
  BookTypeEng,
  PictureRequest,
} from "@/types/acquirebook";
import { generateUniqueId } from "@/utils/generateUniqueId";
import {
  applyZenbujikoGenzaiTransform,
  transformChibanKaokuNumber,
} from "@/utils/utilsAcquireBook";
import type { BookRequestCheckboxStatuses } from "../types";

interface PictureRequestPayload {
  withOwnerAnalyze: boolean;
  withAllMattersAnalyze: boolean;
  identificationName: string;
  /**
   * Salesforce起点のリクエストであることを示すレコードID。
   * リクエスト単位で1つ持つため、items（物件単位）ではなくこちらに持たせる。
   */
  salesforceMapSearchId?: string;
  items: PictureRequest[];
}

interface PictureRequestEstimateItem extends Omit<PictureRequest, "unique_id"> {
  unique_id?: string;
}

interface PictureRequestEstimatePayload
  extends Omit<PictureRequestPayload, "items"> {
  items: PictureRequestEstimateItem[];
  reuseOnlyOwn: boolean;
}

const validBookTypes: BookTypeEng[] = [
  "LAND",
  "BUILDING",
  "CONDOMINIUM",
  "ONE_BUILDING",
];

const buildBasePictureRequest = (
  data: AcquireMultipleBookHandoffData,
  checkboxStates: BookRequestCheckboxStatuses,
  pastDays: number | ""
): Omit<PictureRequest, "unique_id"> => {
  const pictures: AcquireBookPictures[] = [];
  let zenbujikoOptions: AcquireBookZenbujiko[] = [];

  for (const key in checkboxStates) {
    switch (key) {
      case "ownerInfo":
        if (checkboxStates[key].checked) {
          pictures.push("SHOYUSHAJIKO");
        }
        break;
      case "sintaku":
        if (checkboxStates[key].checked) {
          zenbujikoOptions.push("SHINTAKUMOKUROKU");
        }
        break;
      case "kyoutan":
        if (checkboxStates[key].checked) {
          zenbujikoOptions.push("KYODOTANPOMOKUROKU");
        }
        break;
      case "requestClosureBook":
        if (checkboxStates[key].checked) {
          zenbujikoOptions.push("HEISATOUKIBO");
        }
        break;
      case "tisekiDrawings":
        if (checkboxStates[key].checked) {
          pictures.push("CHISEKISOKURYOZU");
        }
        break;
      case "electricDrawings":
        if (checkboxStates[key].checked) {
          pictures.push("DENSHIKOZU");
        }
        break;
      case "tiekiDrawings":
        if (checkboxStates[key].checked) {
          pictures.push("CHIEKIKENZUMEN");
        }
        break;
      case "buildingDrawings":
        if (checkboxStates[key].checked) {
          pictures.push("TATEMONOZUMEN");
        }
        break;
    }
  }

  if (
    checkboxStates.sintaku.checked ||
    checkboxStates.kyoutan.checked ||
    checkboxStates.requestClosureBook.checked
  ) {
    pictures.push("ZENBUJIKO");
  }

  if (checkboxStates.genzai.checked) {
    zenbujikoOptions = applyZenbujikoGenzaiTransform(zenbujikoOptions);
  }

  return {
    real_estate_type: data.bookType,
    pref_code: data.prefCode,
    location_full: data.locationFull,
    chiban_kaoku_number: transformChibanKaokuNumber(data.chibanKaokuNumber),
    past_days: pastDays,
    pictures,
    zenbujiko_options: zenbujikoOptions,
    reuse_pictures: [],
  };
};

const validateRow = (row: AcquireMultipleBookHandoffData): boolean => {
  return (
    validBookTypes.includes(row.bookType) &&
    row.prefCode !== "" &&
    row.locationFull !== "" &&
    row.chibanKaokuNumber !== ""
  );
};

const hasSelectedRequest = (
  request: Pick<PictureRequest, "pictures" | "zenbujiko_options">
): boolean => {
  return request.pictures.length + request.zenbujiko_options.length !== 0;
};

export const makePictureRequestItems = (
  rows: AcquireMultipleBookHandoffData[],
  checkBoxOptions: BookRequestCheckboxStatuses[],
  pastDays: number | ""
): PictureRequest[] => {
  return rows
    .filter(validateRow)
    .map((value, index) => {
      return {
        ...buildBasePictureRequest(value, checkBoxOptions[index], pastDays),
        unique_id: generateUniqueId(),
      };
    })
    .filter(hasSelectedRequest);
};

export const makePictureRequestEstimateItems = (
  rows: AcquireMultipleBookHandoffData[],
  checkBoxOptions: BookRequestCheckboxStatuses[],
  pastDays: number | ""
): PictureRequestEstimateItem[] => {
  return rows
    .filter(validateRow)
    .map((value, index) => {
      return buildBasePictureRequest(value, checkBoxOptions[index], pastDays);
    })
    .filter(hasSelectedRequest);
};

export const makePictureRequestPayload = (
  rows: AcquireMultipleBookHandoffData[],
  checkBoxOptions: BookRequestCheckboxStatuses[],
  pastDays: number | "",
  withOwnerAnalyze: boolean,
  withAllMattersAnalyze: boolean,
  identificationName: string,
  allMattersAnalyzeOptions?: AllMattersAnalyzeOptions,
  salesforceMapSearchId?: string | null
): PictureRequestPayload => {
  return {
    withOwnerAnalyze,
    withAllMattersAnalyze,
    identificationName,
    ...(withAllMattersAnalyze && allMattersAnalyzeOptions),
    ...(salesforceMapSearchId ? { salesforceMapSearchId } : {}),
    items: makePictureRequestItems(rows, checkBoxOptions, pastDays),
  };
};

export const makePictureRequestEstimatePayload = (
  rows: AcquireMultipleBookHandoffData[],
  checkBoxOptions: BookRequestCheckboxStatuses[],
  pastDays: number | "",
  withOwnerAnalyze: boolean,
  withAllMattersAnalyze: boolean,
  reuseOnlyOwn: boolean,
  identificationName: string
): PictureRequestEstimatePayload => {
  return {
    withOwnerAnalyze,
    withAllMattersAnalyze,
    reuseOnlyOwn,
    identificationName,
    items: makePictureRequestEstimateItems(rows, checkBoxOptions, pastDays),
  };
};

export type { PictureRequestEstimatePayload, PictureRequestPayload };
